import { registerComponent, debounce } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "wyatt-slider",
  class WyattSlider extends WYATTElement {
    constructor() {
      super();

      this.currentIndex = this.config.startAt || 0;
      this.slider = this.querySelector("[data-slider]");
      this.slides = Array.from(this.slider.children);
      this.navigation = null;
      this.navigationButtons = [];
      this.slideCount = this.slides.length;
      this.slidesToHideCount = 0;
      this.navigationItemsToShow = this.slideCount;
      this.initializedSlider = false;
      this.sliderGap =
        parseFloat(window.getComputedStyle(this.slider).gap) || 0;
    }

    connectedCallback() {
      this.setupData();
      this.setupNavigation();
      this.setupControls();
      this.bindEvents();
      this.connectSliders();
      this.initializeSlider();
    }

    // SETUP

    setupData() {
      this.slides.forEach((slide, index) => {
        slide.dataset.slide = index;
        slide.classList.add("slider__slide");
      });
    }

    setupNavigation() {
      if (!this.config.showNavigation) {
        return;
      }

      this.navigation = document.createElement("div");
      const navigationButton = this.querySelector(
        "[data-navigation-button]",
      ).content;

      this.slides.forEach((slide, index) => {
        const button = navigationButton.cloneNode(true);

        button.querySelector(".slider__dot").textContent = `Slide ${index + 1}`;
        button.querySelector(
          "[data-slider-navigation-button]",
        ).dataset.sliderNavigationButton = index;

        this.navigation.appendChild(button);
      });

      this.navigation.classList.add("slider__navigation");
      this.appendChild(this.navigation);

      this.navigationButtons = this.navigation.querySelectorAll(
        "[data-slider-navigation-button]",
      );
    }

    setupControls() {
      if (this.config.hideControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);

      if (this.config.showNavigation) {
        this.navigation.appendChild(controls);
      } else {
        this.appendChild(controls);
      }

      this.setAttribute("has-controls", true);
      this.controlButtons = this.querySelectorAll(
        "[data-slider-control-button]",
      );
    }

    bindEvents() {
      if (!this.config.hideControls) {
        this.bindControlEvents();
      }

      if (this.config.showNavigation) {
        this.bindNavigationEvents();
      }

      if ("onscrollsnapchanging" in this.slider) {
        this.slider.addEventListener("scrollsnapchanging", (event) => {
          this.handleScrollSnapChanging(event);
        });
      } else {
        this.slider.addEventListener(
          "scroll",
          debounce((event) => {
            this.handleScroll(event);
          }, 300).bind(this),
        );
      }

      window.addEventListener(
        "resize",
        debounce(() => {
          this.handleResize();
        }, 300),
      );

      this.subscribe(WYATT.PubSub.events.containerShown, () => {
        if (this.initializedSlider || this.slider.offsetParent === null) {
          return;
        }

        this.initializeSlider();
      });
    }

    connectSliders() {
      const sliderToNavigate = document.querySelector(
        this.config.navigationFor,
      );

      if (sliderToNavigate) {
        this.sliderToNavigate = sliderToNavigate;
        sliderToNavigate.navigatedBy = this;

        this.slides.forEach((slide) => {
          this.updateSlideAsNavigation(slide);

          slide
            .querySelector("button")
            .addEventListener(
              "click",
              this.handleSlideAsNavigationClick.bind(this),
            );
        });
      }
    }

    initializeSlider() {
      if (this.sliderIsVisible()) {
        this.initializedSlider = true;

        this.updateNavigationItemsToShow(this.currentIndex);
        this.updateNavigationButtons(this.currentIndex);
        this.updateControlButtons(this.currentIndex);
        this.goTo(this.config.startAt, "instant");
        this.setAttribute("ready", true);
      }
    }

    // UPDATES

    updateNavigationItemsToShow(newIndex) {
      const reversedSlides = [...this.slides].reverse();
      const containerWidth = this.getVisibleWidth();
      const currentVisibleSlidesCount = Array.from(
        this.navigationButtons,
      ).filter((slide) => !slide.hasAttribute("hidden")).length;

      this.slidesToHideCount = 0;
      let accumulatedWidth = reversedSlides[0].getBoundingClientRect().width;

      reversedSlides.slice(1).some((slide) => {
        const slideWidth = slide.getBoundingClientRect().width + this.sliderGap;
        accumulatedWidth += slideWidth;
        if (accumulatedWidth > containerWidth) {
          return true;
        }
        this.slidesToHideCount += 1;
        return false;
      });

      this.navigationItemsToShow = Math.max(
        1,
        this.slides.length - this.slidesToHideCount,
      );

      this.currentIndex = Math.min(newIndex, this.navigationItemsToShow - 1);

      if (currentVisibleSlidesCount !== this.navigationItemsToShow) {
        this.navigationButtons.forEach((button, index) => {
          button.hidden = index >= this.navigationItemsToShow;
        });
        this.updateState(this.currentIndex);
      }
    }

    updateCurrentSlide(newIndex) {
      this.slides[this.currentIndex].setAttribute("active", false);
      this.currentIndex = newIndex;
      this.slides[this.currentIndex].setAttribute("active", true);
    }

    updateNavigationButtons(newIndex) {
      this.hideNavigation = this.slider.offsetWidth >= this.slider.scrollWidth;

      if (this.hideNavigation) {
        this.navigation?.setAttribute("hidden", true);
      } else {
        this.navigation?.removeAttribute("hidden");
      }

      this.navigationButtons?.forEach((button) => {
        button.setAttribute("active", false);
      });

      this.navigationButtons?.[
        Math.min(newIndex, this.navigationItemsToShow - 1)
      ]?.setAttribute("active", true);
    }

    updateControlButtons(newIndex) {
      if (this.config.hideControls) {
        return;
      }

      this.hideControls = this.slider.offsetWidth >= this.slider.scrollWidth;

      if (this.hideControls) {
        this.removeAttribute("has-controls");
      } else {
        this.setAttribute("has-controls", true);
      }

      this.controlButtons.forEach((button) => {
        button.hidden = this.hideControls;

        const direction = button.dataset.sliderControlButton;
        const onFirstSlide = newIndex === 0;
        const onLastSlide = newIndex === this.navigationItemsToShow - 1;

        if (direction === "previous" && onFirstSlide) {
          button.disabled = true;
        } else if (direction === "next" && onLastSlide) {
          button.disabled = true;
        } else {
          button.disabled = false;
        }
      });
    }

    updateSlideAsNavigation(slide) {
      const button = document.createElement("button");
      const html = slide.innerHTML;

      button.type = "button";
      button.classList.add("button-reset", "slider__slide-button");
      button.innerHTML = html;

      slide.innerHTML = "";
      slide.appendChild(button);
    }

    updateState(newIndex) {
      this.updateNavigationButtons(newIndex);
      this.updateCurrentSlide(newIndex);
      this.updateControlButtons(newIndex);
    }

    // EVENT BINDINGS

    bindControlEvents() {
      if (!this.controlButtons) {
        return;
      }

      this.controlButtons.forEach((button) => {
        button.addEventListener("click", this.handleControlClick.bind(this));
      });
    }

    bindNavigationEvents() {
      if (!this.navigation) {
        return;
      }

      this.navigationButtons.forEach((button) => {
        button.addEventListener("click", this.handleNavigationClick.bind(this));
      });
    }

    // EVENT HANDLERS

    handleResize() {
      if (this.sliderIsVisible() && this.initializedSlider) {
        this.updateNavigationItemsToShow(this.currentIndex);
      } else {
        this.initializedSlider = false;
      }
    }

    handleControlClick(event) {
      const button = event.currentTarget;
      const direction = button.dataset.sliderControlButton;
      const newIndex =
        direction === "next" ? this.currentIndex + 1 : this.currentIndex - 1;

      this.goTo(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }

      if (this.sliderToNavigate) {
        this.sliderToNavigate.goTo(newIndex);
      }
    }

    handleNavigationClick(event) {
      const button = event.currentTarget;
      const newIndex = parseInt(button.dataset.sliderNavigationButton, 10);

      this.goTo(newIndex);
    }

    handleScroll(event) {
      if (this === this.sliderToNavigate?.navigatedBy) {
        return;
      }

      const newIndex = this.calculateCurrentIndex();

      if (newIndex === this.currentIndex) {
        return;
      }

      event.preventDefault();

      this.updateState(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }
    }

    handleScrollSnapChanging(event) {
      if (this === this.sliderToNavigate?.navigatedBy) {
        return;
      }

      const slideIndex = parseInt(event.snapTargetInline.dataset.slide, 10);
      const newIndex = Math.min(slideIndex, this.navigationItemsToShow - 1);

      this.updateState(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }
    }

    handleSlideAsNavigationClick(event) {
      const slide = event.currentTarget.closest("[data-slide]");
      const index = parseInt(slide.dataset.slide, 10);

      event.preventDefault();

      this.sliderToNavigate.goTo(index);
      this.updateState(index);
    }

    // HELPERS

    sliderIsVisible() {
      return this.slider.offsetParent !== null && this.slider.offsetWidth > 0;
    }

    calculateCurrentIndex() {
      const { scrollLeft, scrollWidth, offsetWidth } = this.slider;
      const maxScroll = scrollWidth - offsetWidth;

      // At the end of scroll, return the last navigation index
      if (scrollLeft >= maxScroll - 1) {
        return this.slideCount - 1;
      }

      // At the start, return 0
      if (scrollLeft <= 1) {
        return 0;
      }

      // Calculate based on scroll progress through available scroll range
      const scrollProgress = scrollLeft / maxScroll;
      const index = Math.round(scrollProgress * (this.slideCount - 1));

      return Math.min(index, this.slideCount - 1);
    }

    getVisibleWidth() {
      const styles = getComputedStyle(this.slider);
      const scrollPadding =
        parseFloat(styles.scrollPaddingInline) ||
        parseFloat(styles.scrollPaddingLeft) ||
        0;

      return this.slider.getBoundingClientRect().width - scrollPadding;
    }

    goTo(newIndex, behavior = "smooth") {
      const newSlide = this.slider.querySelector(`[data-slide="${newIndex}"]`);
      const width = newSlide.offsetWidth;
      const slideDifference = newIndex - this.currentIndex;

      if (!newSlide) {
        return;
      }

      this.slider.scrollBy({
        top: 0,
        left: width * slideDifference,
        behavior,
      });

      this.updateState(newIndex);
    }
  },
);
